// app/javascript/controllers/client_combobox_controller.js
import { Controller } from "@hotwired/stimulus"
import { useClickOutside } from "stimulus-use"

export default class extends Controller {
  static targets = [
    "input", "hiddenInput", "optionsList", "optionsContainer", "separator",
    "createOption", "loadingIndicator", "modalErrorContainer", "modalFormContainer"
  ]

  static values = {
    searchUrl: String,
    createUrl: String,
    selectedClient: String,
    selectedText: String
  }

  connect() {
    useClickOutside(this, { element: this.element })
    
    this.highlightedIndex = -1
    this.currentOptions = []
    this.searchController = null
    this.searchTimeout = null
    this.blurTimeout = null
    this._positioned = false
    
    // Bind pour les event listeners
    this._updatePosition = this.updatePosition.bind(this)
    
    // Initialiser avec la valeur sélectionnée
    if (this.selectedClientValue) {
      this.hiddenInputTarget.value = this.selectedClientValue
      this.inputTarget.value = this.selectedTextValue
    }
  }

  disconnect() {
    this.cleanupPositioning()
    
    if (this.searchController) {
      this.searchController.abort()
    }
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
    if (this.blurTimeout) {
      clearTimeout(this.blurTimeout)
    }
  }

  toggle(event) {
    event.preventDefault()
    
    // Si la modal de création est ouverte, ne pas toggler
    const dialog = document.getElementById('client-combobox-create-modal')
    if (dialog && dialog.open) {
      return
    }
    
    if (this.optionsListTarget.classList.contains("hidden")) {
      this.loadClients()
    } else {
      this.close()
    }
  }

  focus() {
    // Annuler tout timeout de blur en cours
    if (this.blurTimeout) {
      clearTimeout(this.blurTimeout)
      this.blurTimeout = null
    }
    
    if (this.optionsListTarget.classList.contains("hidden")) {
      this.loadClients()
    }
  }

  blur() {
    // Annuler tout timeout de blur précédent
    if (this.blurTimeout) {
      clearTimeout(this.blurTimeout)
    }
    
    // Délai pour permettre la sélection d'une option
    this.blurTimeout = setTimeout(() => {
      if (!this.element.contains(document.activeElement)) {
        this.close()
      }
      this.blurTimeout = null
    }, 150)
  }

  clickOutside(event) {
    this.close()
  }

  close() {
    this.optionsListTarget.classList.add("hidden")
    this.unhighlightAll()
    this.highlightedIndex = -1
    this.hideOptions()
  }

  search() {
    // Annuler le timeout précédent
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
    
    // Programmer une nouvelle recherche avec debouncing
    this.searchTimeout = setTimeout(() => {
      this.performSearch()
    }, 300)
  }

  async performSearch() {
    const query = this.inputTarget.value.trim()
    
    // Annuler la recherche précédente
    if (this.searchController) {
      this.searchController.abort()
    }
    
    this.showLoading()
    
    try {
      this.searchController = new AbortController()
      
      const url = new URL(this.searchUrlValue, window.location.origin)
      if (query) {
        url.searchParams.set('q', query)
      }
      
      const response = await fetch(url, {
        signal: this.searchController.signal,
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      
      if (!response.ok) {
        throw new Error('Erreur de recherche')
      }
      
      const clients = await response.json()
      this.displayClients(clients)
      
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Erreur lors de la recherche:', error)
        this.displayError('Erreur lors de la recherche des clients')
      }
    } finally {
      this.hideLoading()
    }
  }

  async loadClients() {
    // Toujours déclencher une recherche, même si l'input n'est pas vide
    // Cela permet d'afficher les résultats pertinents lors de l'édition
    this.optionsListTarget.classList.remove("hidden")
    this.showOptions()
    await this.performSearch()
  }

  displayClients(clients) {
    this.currentOptions = clients
    this.optionsListTarget.classList.remove("hidden")
    this.showOptions()
    
    // Vider le conteneur des options
    this.optionsContainerTarget.innerHTML = ''
    
    if (clients.length === 0) {
      this.optionsContainerTarget.innerHTML = `
        <li class="relative py-2 pl-3 pr-9 text-gray-500">
          <span class="block">Aucun client trouvé</span>
        </li>
      `
      this.separatorTarget.classList.add("hidden")
    } else {
      clients.forEach((client, index) => {
        const li = document.createElement('li')
        li.className = "relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-900 hover:bg-sz-primary-600 hover:text-white group"
        li.dataset.clientComboboxTarget = "option"
        li.dataset.action = "click->client-combobox#select"
        li.dataset.value = client.id
        li.dataset.index = index
        
        const emailText = client.email ? ` - ${client.email}` : ''
        const birthDateText = client.birth_date ? `<span class="text-xs text-gray-400 group-hover:text-white/70 ml-1">(${client.birth_date})</span>` : ''
        li.innerHTML = `
          <span class="block truncate">
            ${client.full_name}${birthDateText}
            <span class="text-gray-500 group-hover:text-white/80">${emailText}</span>
          </span>
        `
        
        this.optionsContainerTarget.appendChild(li)
      })
      this.separatorTarget.classList.remove("hidden")
    }
  }

  displayError(message) {
    this.optionsContainerTarget.innerHTML = `
      <li class="relative py-2 pl-3 pr-9 text-red-500">
        <span class="block">${message}</span>
      </li>
    `
    this.separatorTarget.classList.add("hidden")
    this.optionsListTarget.classList.remove("hidden")
    this.showOptions()
  }

  showLoading() {
    this.loadingIndicatorTarget.classList.remove("hidden")
  }

  hideLoading() {
    this.loadingIndicatorTarget.classList.add("hidden")
  }

  select(event) {
    const selectedOption = event.currentTarget
    const clientId = selectedOption.dataset.value
    const client = this.currentOptions.find(c => c.id == clientId)
    
    if (client) {
      this.inputTarget.value = client.full_name
      this.hiddenInputTarget.value = client.id
      
      // Déclencher un événement pour notifier le changement
      this.hiddenInputTarget.dispatchEvent(new Event('change', { bubbles: true }))
    }
    
    this.close()
  }

  navigate(event) {
    const visibleOptions = this.optionsContainerTarget.querySelectorAll('li[data-client-combobox-target="option"]')
    
    if (this.optionsListTarget.classList.contains("hidden")) {
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault()
        this.loadClients()
        return
      }
    }

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.highlightNext(visibleOptions)
        break
      case "ArrowUp":
        event.preventDefault()
        this.highlightPrevious(visibleOptions)
        break
      case "Enter":
        event.preventDefault()
        if (this.highlightedIndex >= 0 && this.highlightedIndex < visibleOptions.length) {
          visibleOptions[this.highlightedIndex].click()
        }
        break
      case "Escape":
        this.close()
        break
    }
  }

  highlightNext(options) {
    this.unhighlightAll()
    this.highlightedIndex++
    if (this.highlightedIndex >= options.length) {
      this.highlightedIndex = 0
    }
    this.highlight(options[this.highlightedIndex])
  }

  highlightPrevious(options) {
    this.unhighlightAll()
    this.highlightedIndex--
    if (this.highlightedIndex < 0) {
      this.highlightedIndex = options.length - 1
    }
    this.highlight(options[this.highlightedIndex])
  }

  highlight(option) {
    if (option) {
      option.classList.add("bg-sz-primary-600", "text-white")
      option.scrollIntoView({ block: 'nearest' })
    }
  }

  unhighlightAll() {
    this.optionsContainerTarget.querySelectorAll('li').forEach(option => {
      option.classList.remove("bg-sz-primary-600", "text-white")
    })
  }

  showCreateModal(event) {
    event.preventDefault()
    this.close() // Fermer la liste des options
    const dialog = document.getElementById('client-combobox-create-modal')
    if (dialog) {
      dialog.showModal()
    }
    this.clearModalErrors()
    
    // Focus sur le premier champ
    setTimeout(() => {
      const firstInput = dialog?.querySelector('input[type="text"]')
      if (firstInput) firstInput.focus()
    }, 100)
  }

  closeCreateModal(event) {
    event.preventDefault()
    const dialog = document.getElementById('client-combobox-create-modal')
    if (dialog) {
      dialog.close()
    }
    this.clearModalForm()
  }

  clearModalErrors() {
    const errorContainer = document.getElementById('client-combobox-modal-error-container')
    if (errorContainer) {
      errorContainer.innerHTML = ""
    }
  }

  clearModalForm() {
    const dialog = document.getElementById('client-combobox-create-modal')
    if (dialog) {
      const inputs = dialog.querySelectorAll('input[type="text"], input[type="email"], input[type="tel"]')
      inputs.forEach(input => input.value = "")
    }
    this.clearModalErrors()
  }

  handleClientCreated(event) {
    // Récupérer les données depuis l'élément trigger
    const triggerElement = event.target
    const clientId = triggerElement.dataset.clientId
    const clientName = triggerElement.dataset.clientName
    
    if (clientId && clientName) {
      // Mettre à jour la sélection avec le nouveau client
      this.inputTarget.value = clientName
      this.hiddenInputTarget.value = clientId
      
      // Déclencher un événement pour notifier le changement
      this.hiddenInputTarget.dispatchEvent(new Event('change', { bubbles: true }))
      
      // Fermer la modal et nettoyer
      const dialog = document.getElementById('client-combobox-create-modal')
      if (dialog) {
        dialog.close()
      }
      this.clearModalForm()
    }
  }

  handleFormStart(event) {
    const dialog = document.getElementById('client-combobox-create-modal')
    if (dialog) {
      const submitButton = dialog.querySelector('input[type="submit"]')
      if (submitButton) {
        submitButton.disabled = true
        submitButton.value = "Création en cours..."
      }
    }
    this.clearModalErrors()
  }

  handleFormEnd(event) {
    const dialog = document.getElementById('client-combobox-create-modal')
    if (dialog) {
      const submitButton = dialog.querySelector('input[type="submit"]')
      if (submitButton) {
        submitButton.disabled = false
        submitButton.value = "Créer"
      }
    }
    // Les Turbo Streams s'occupent du reste
  }

  // Méthodes pour gérer le portal dans les dialogs
  get dialogEl() {
    return this.element.closest('dialog') || null
  }

  showOptions() {
    this.setupPositioning()
    // Pas besoin d'appeler updatePosition ici car setupPositioning le fait déjà
    window.addEventListener('resize', this._updatePosition, true)
    window.addEventListener('scroll', this._updatePosition, true)
  }

  hideOptions() {
    this.cleanupPositioning()
    window.removeEventListener('resize', this._updatePosition, true)
    window.removeEventListener('scroll', this._updatePosition, true)
  }

  setupPositioning() {
    const dlg = this.dialogEl
    
    if (dlg) {
      // Dans un dialog : ensure the dialog does not clip
      dlg.style.overflow = 'visible'

      // Passer en position fixed pour sortir du flux de clipping
      this.optionsListTarget.style.position = 'fixed'
      this.optionsListTarget.style.zIndex = '2147483647'
      this.optionsListTarget.style.maxHeight = '60vh'
      this.optionsListTarget.style.overflow = 'auto'

      // Remove Tailwind 'absolute' if present; keep other classes
      this.optionsListTarget.classList.remove('absolute')
      this._positioned = true
      
      this.updatePosition()
    } else {
      // Hors dialog : garder le comportement normal
      this._positioned = false
    }
  }

  cleanupPositioning() {
    if (!this._positioned) return
    
    // restore classes/inline styles
    this.optionsListTarget.style.position = ''
    this.optionsListTarget.style.zIndex = ''
    this.optionsListTarget.style.maxHeight = ''
    this.optionsListTarget.style.overflow = ''
    this.optionsListTarget.classList.add('absolute')

    this._positioned = false
  }

  updatePosition() {
    if (!this._positioned) return
    
    const r = this.inputTarget.getBoundingClientRect()

    // Align width and position under the input
    // this.optionsListTarget.style.top = `${r.bottom + 1}px`  // +1 pour un petit gap
    // this.optionsListTarget.style.left = `${r.left}px`
    this.optionsListTarget.style.width = `${r.width}px`
  }
}
